import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, LinearProgress, Button, Alert, IconButton, Tooltip, Stack,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Grid, Divider, Link
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CheckCircle as ApprovedIcon,
  Cancel as RejectedIcon,
  HourglassTop as PendingIcon,
  LocalShipping as PrinterIcon,
  Person as CustomerIcon,
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';

const APP_API_KEY = process.env.REACT_APP_API_KEY || 'e4ccbc0bd680211f4e4553ea3ab07d8a55707e79cde61faa10c51c55c6da35a9';
const API = '/api/logos';
// Direct Supabase upload (anon key — same pattern as the public upload widget)
const SUPABASE_URL = 'https://mjdzdtuxosnalftlcuxa.supabase.co';
const SUPABASE_ANON = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im1qZHpkdHV4b3NuYWxmdGxjdXhhIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjgwNTEzNTIsImV4cCI6MjA4MzYyNzM1Mn0.NnQ4uHGpqre_9aDdBa-ySNn0AES8mQ7OhPiONxlu018';
const STORAGE_BUCKET = 'logo-uploads';

// Friendly download filename — matches the format used in drukker batch ZIPs.
function suggestedFilename(j) {
  if (!j) return 'result.pdf';
  const safe = (s) => String(s || '').replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 40);
  return `order_${j.order_id || 'x'}_${safe(j.company)}_SPF${j.spf}_${j.color}_${j.language}.pdf`;
}

async function uploadToStorage(file, objectPath) {
  const url = `${SUPABASE_URL}/storage/v1/object/${STORAGE_BUCKET}/${objectPath}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { Authorization: `Bearer ${SUPABASE_ANON}`, apikey: SUPABASE_ANON, 'Content-Type': file.type || 'application/pdf' },
    body: file,
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`Storage upload ${res.status}: ${body.slice(0, 200)}`);
  }
  return objectPath;
}

function newUuid() {
  return (window.crypto && window.crypto.randomUUID)
    ? window.crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = Math.random() * 16 | 0; return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
      });
}

// Workflow filter chips. label = wat HITL ziet, value = workflow_status filter, count_key = key in /counts response.
const FILTERS = [
  { label: 'Pending review',  value: 'pending_review',    icon: <PendingIcon fontSize="small" />,  countKey: 'pending_review' },
  { label: 'Wacht op klant',  value: 'awaiting_customer', icon: <CustomerIcon fontSize="small" />, countKey: 'awaiting_customer' },
  { label: 'Klaar voor druk', value: 'ready_for_print',   icon: <PrinterIcon fontSize="small" />,  countKey: 'ready_for_print' },
  { label: 'Afgekeurd',       value: 'rejected',          icon: <RejectedIcon fontSize="small" />, countKey: 'rejected' },
  { label: 'Naar drukker',    value: 'sent_to_printer',   icon: <ApprovedIcon fontSize="small" />, countKey: 'sent_to_printer' },
  { label: 'Afgerond',        value: 'completed',         icon: <ApprovedIcon fontSize="small" />, countKey: 'completed' },
  { label: 'Alles',           value: '',                  icon: null,                              countKey: 'all' },
];
// Note: cancelled WC orders still flip to workflow_status='cancelled' (lazy sweep) so the active
// queue stays clean. The chip is intentionally hidden — those rows are reachable via 'Alles' if needed.

function statusChip(job) {
  if (job.workflow_status) {
    const f = FILTERS.find(x => x.value === job.workflow_status);
    return <Chip size="small" label={f ? f.label : job.workflow_status} />;
  }
  // No workflow_status set: fall back to processing/error/done
  if (job.status === 'error') return <Chip size="small" label="Error" color="error" />;
  if (job.status === 'done') return <Chip size="small" label="Done (geen review-state)" />;
  return <Chip size="small" label={job.status} variant="outlined" />;
}

function formatDpi(job) {
  if (!job.dpi_before) return '-';
  const before = Number(job.dpi_before).toFixed(0);
  if (job.dpi_after) {
    return `${before} → ${Number(job.dpi_after).toFixed(0)} (upscaled)`;
  }
  if (Number(job.dpi_before) < 200) return `${before} ⚠`;
  return before;
}

function authHeaders() {
  return { 'x-api-key': APP_API_KEY };
}

async function apiCall(path, options = {}) {
  const res = await fetch(`${API}${path}`, {
    ...options,
    headers: { ...authHeaders(), 'Content-Type': 'application/json', ...(options.headers || {}) },
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`HTTP ${res.status}: ${body.slice(0, 200)}`);
  }
  return res.json();
}

export default function Logos() {
  const [filter, setFilter] = useState('pending_review');
  const [jobs, setJobs] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detailJob, setDetailJob] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [actionPending, setActionPending] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showReject, setShowReject] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = filter ? `?workflow_status=${filter}&limit=100` : '?limit=100';
      const [data, countsData] = await Promise.all([
        apiCall(qs),
        apiCall('/counts').catch(() => ({})),
      ]);
      setJobs(data.jobs || []);
      setCounts(countsData || {});
    } catch (err) {
      setError(err.message);
      setJobs([]);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  async function openDetail(jobId) {
    setDetailJob({ id: jobId });
    setDetailLoading(true);
    setDetailError(null);
    setShowReject(false);
    setRejectReason('');
    try {
      const data = await apiCall(`/${jobId}`);
      setDetailJob(data);
    } catch (err) {
      setDetailError(err.message);
    } finally {
      setDetailLoading(false);
    }
  }

  function closeDetail() {
    setDetailJob(null);
    setShowReject(false);
    setRejectReason('');
    setShowDeleteConfirm(false);
    setDeleteConfirmText('');
  }

  async function deleteJob() {
    if (!detailJob?.id) return;
    setActionPending(true);
    try {
      await apiCall(`/${detailJob.id}`, { method: 'DELETE' });
      closeDetail();
      await fetchJobs();
    } catch (err) {
      setDetailError(err.message);
    } finally {
      setActionPending(false);
    }
  }

  async function approve() {
    if (!detailJob?.id) return;
    setActionPending(true);
    try {
      await apiCall(`/${detailJob.id}/approve`, { method: 'POST' });
      closeDetail();
      await fetchJobs();
    } catch (err) {
      setDetailError(err.message);
    } finally {
      setActionPending(false);
    }
  }

  async function reject() {
    if (!detailJob?.id) return;
    setActionPending(true);
    try {
      await apiCall(`/${detailJob.id}/reject`, {
        method: 'POST',
        body: JSON.stringify({ reason: rejectReason || null }),
      });
      closeDetail();
      await fetchJobs();
    } catch (err) {
      setDetailError(err.message);
    } finally {
      setActionPending(false);
    }
  }

  // Drukker batch state
  const [batchPreview, setBatchPreview] = useState(null);
  const [batchOpen, setBatchOpen] = useState(false);
  const [batchSending, setBatchSending] = useState(false);
  const [batchResult, setBatchResult] = useState(null);

  async function openBatchDialog() {
    setBatchOpen(true);
    setBatchPreview(null);
    setBatchResult(null);
    try {
      const data = await apiCall('/batch/preview');
      setBatchPreview(data);
    } catch (err) {
      setBatchPreview({ error: err.message });
    }
  }

  async function sendBatch() {
    setBatchSending(true);
    try {
      const result = await apiCall('/batch-to-printer', { method: 'POST' });
      setBatchResult(result);
      await fetchJobs();
    } catch (err) {
      setBatchResult({ error: err.message });
    } finally {
      setBatchSending(false);
    }
  }

  function closeBatch() {
    setBatchOpen(false);
    setBatchPreview(null);
    setBatchResult(null);
  }

  // Manual / replace upload state
  const [manualOpen, setManualOpen] = useState(false);
  const [manualForm, setManualForm] = useState({ order_id: '', company: '', spf: '50', color: 'white', language: 'NL', skip_review: false });
  const [manualFile, setManualFile] = useState(null);
  const [manualBusy, setManualBusy] = useState(false);
  const [manualErr, setManualErr] = useState(null);

  async function submitManual() {
    if (!manualFile) { setManualErr('Selecteer een PDF'); return; }
    const cleanOrderId = manualForm.order_id.trim().replace(/^#/, '');
    if (!cleanOrderId || !manualForm.company.trim()) { setManualErr('order_id en bedrijf zijn verplicht'); return; }
    setManualBusy(true);
    setManualErr(null);
    try {
      const safeOrder = cleanOrderId.replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 64);
      const path = `results/${safeOrder}/manual-${newUuid()}.pdf`;
      await uploadToStorage(manualFile, path);
      await apiCall('/manual', {
        method: 'POST',
        body: JSON.stringify({ ...manualForm, order_id: cleanOrderId, result_path: path }),
      });
      setManualOpen(false);
      setManualForm({ order_id: '', company: '', spf: '50', color: 'white', language: 'NL', skip_review: false });
      setManualFile(null);
      await fetchJobs();
    } catch (err) {
      setManualErr(err.message);
    } finally {
      setManualBusy(false);
    }
  }

  // Replace-result upload (revisie) on an existing detail job
  const [replaceFile, setReplaceFile] = useState(null);
  const [replaceBusy, setReplaceBusy] = useState(false);

  async function submitReplace() {
    if (!detailJob?.id || !replaceFile) return;
    setReplaceBusy(true);
    try {
      const safeOrder = (detailJob.order_id || 'unknown').replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 64);
      const path = `results/${safeOrder}/revision-${newUuid()}.pdf`;
      await uploadToStorage(replaceFile, path);
      await apiCall(`/${detailJob.id}/replace-result`, {
        method: 'POST',
        body: JSON.stringify({ result_path: path }),
      });
      setReplaceFile(null);
      closeDetail();
      await fetchJobs();
    } catch (err) {
      setDetailError(err.message);
    } finally {
      setReplaceBusy(false);
    }
  }

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5">Logo's <Typography component="span" variant="body2" color="text.secondary">— HITL workflow</Typography></Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => setManualOpen(true)}>
            + Handmatige job
          </Button>
          <Button variant="contained" color="warning" startIcon={<PrinterIcon />} onClick={openBatchDialog}>
            Batch naar drukker
          </Button>
          <Tooltip title="Vernieuwen">
            <IconButton onClick={fetchJobs} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        {FILTERS.map(f => {
          const count = counts && typeof counts[f.countKey] === 'number' ? counts[f.countKey] : null;
          const labelText = count !== null ? `${f.label} (${count})` : f.label;
          const isActive = filter === f.value;
          return (
            <Chip
              key={f.value || 'all'}
              label={labelText}
              icon={f.icon || undefined}
              onClick={() => setFilter(f.value)}
              color={isActive ? 'primary' : (count > 0 ? 'warning' : 'default')}
              variant={isActive ? 'filled' : 'outlined'}
            />
          );
        })}
      </Stack>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>Kon jobs niet laden: {error}</Alert>}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Order#</TableCell>
              <TableCell>Klant</TableCell>
              <TableCell>SPF</TableCell>
              <TableCell>Kleur</TableCell>
              <TableCell>Taal</TableCell>
              <TableCell>DPI</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Geüpload</TableCell>
              <TableCell align="right">Acties</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {jobs.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Geen jobs in deze status.
                </TableCell>
              </TableRow>
            )}
            {jobs.map((job) => (
              <TableRow key={job.id} hover>
                <TableCell><strong>{job.order_id}</strong>{job.wc_order_id ? <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>wc:{job.wc_order_id}</Typography> : null}</TableCell>
                <TableCell>{job.company}</TableCell>
                <TableCell>{job.spf}</TableCell>
                <TableCell>{job.color}</TableCell>
                <TableCell>{job.language}</TableCell>
                <TableCell>{formatDpi(job)}</TableCell>
                <TableCell>{statusChip(job)}</TableCell>
                <TableCell>{new Date(job.created_at).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' })}</TableCell>
                <TableCell align="right">
                  <Button size="small" onClick={() => openDetail(job.id)}>Detail</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={!!detailJob} onClose={closeDetail} maxWidth="lg" fullWidth>
        <DialogTitle>
          {detailJob ? `Order ${detailJob.order_id || ''} — ${detailJob.company || ''}` : 'Detail'}
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading && <LinearProgress />}
          {detailError && <Alert severity="error" sx={{ mb: 2 }}>{detailError}</Alert>}
          {detailJob && !detailLoading && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>Origineel logo</Typography>
                {detailJob.source_url ? (
                  <Box sx={{ border: '1px solid #eee', p: 1, textAlign: 'center', bgcolor: '#fafafa' }}>
                    <img src={detailJob.source_url} alt="source" style={{ maxWidth: '100%', maxHeight: 360, objectFit: 'contain' }} />
                    <Box sx={{ mt: 1 }}>
                      <Link href={detailJob.source_url} target="_blank" rel="noreferrer" sx={{ mr: 2 }}><OpenInNewIcon fontSize="inherit" /> Open</Link>
                      <Link href={detailJob.source_url} download><DownloadIcon fontSize="inherit" /> Download</Link>
                    </Box>
                  </Box>
                ) : <Alert severity="info">Geen source URL</Alert>}
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>Resultaat-PDF</Typography>
                {detailJob.result_url ? (
                  <Box sx={{ border: '1px solid #eee', p: 1, textAlign: 'center', bgcolor: '#fafafa' }}>
                    <iframe src={detailJob.result_url} title="result" style={{ width: '100%', height: 360, border: 0 }} />
                    <Box sx={{ mt: 1 }}>
                      <Link href={detailJob.result_url} target="_blank" rel="noreferrer" sx={{ mr: 2 }}><OpenInNewIcon fontSize="inherit" /> Open in nieuw tab</Link>
                      <Link href={detailJob.result_url} download={suggestedFilename(detailJob)}><DownloadIcon fontSize="inherit" /> Download PDF</Link>
                    </Box>
                  </Box>
                ) : <Alert severity="info">Geen result URL — PDF nog niet gegenereerd?</Alert>}
                <Box sx={{ mt: 2, p: 1.5, border: '1px dashed #ccc', borderRadius: 1, bgcolor: '#fffbe6' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>Vervang met handmatige PDF (revisie)</Typography>
                  <Box sx={{ mt: 1 }}>
                    <input type="file" accept="application/pdf,.pdf" onChange={e => setReplaceFile(e.target.files?.[0] || null)} />
                    <Button size="small" variant="contained" sx={{ ml: 1 }} disabled={!replaceFile || replaceBusy} onClick={submitReplace}>
                      {replaceBusy ? 'Uploaden...' : 'Vervang PDF'}
                    </Button>
                  </Box>
                  <Typography variant="caption" color="text.secondary">Reset workflow naar Pending review.</Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Divider />
                <Stack direction="row" spacing={3} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
                  <Box><Typography variant="caption">SPF</Typography><Typography>{detailJob.spf}</Typography></Box>
                  <Box><Typography variant="caption">Kleur</Typography><Typography>{detailJob.color}</Typography></Box>
                  <Box><Typography variant="caption">Taal</Typography><Typography>{detailJob.language}</Typography></Box>
                  <Box><Typography variant="caption">DPI (vóór)</Typography><Typography>{formatDpi(detailJob)}</Typography></Box>
                  <Box><Typography variant="caption">Status</Typography><Typography>{statusChip(detailJob)}</Typography></Box>
                  {detailJob.wc_order_id && <Box><Typography variant="caption">WC id</Typography><Typography>{detailJob.wc_order_id}</Typography></Box>}
                </Stack>
                {detailJob.error_message && <Alert severity="warning" sx={{ mt: 2 }}>Notitie: {detailJob.error_message}</Alert>}
              </Grid>
              {showReject && (
                <Grid item xs={12}>
                  <TextField
                    label="Reden van afkeuring (optioneel, gaat niet naar klant)"
                    multiline
                    minRows={2}
                    fullWidth
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                  />
                </Grid>
              )}
              {showDeleteConfirm && (
                <Grid item xs={12}>
                  <Alert severity="error" sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>Definitief verwijderen?</Typography>
                    Deze actie verwijdert:
                    <ul style={{ margin: '4px 0 8px 18px' }}>
                      <li>De job-rij in de database</li>
                      <li>Het geüploade logo (bron) uit storage</li>
                      <li>De gegenereerde drukproef-PDF uit storage</li>
                      <li>Bijbehorende klant-akkoord-tokens (cascade)</li>
                    </ul>
                    De klant-mail die al verstuurd is, blijft staan — de magic-link werkt daarna niet meer.<br/>
                    <strong>Dit kan niet ongedaan worden gemaakt.</strong>
                  </Alert>
                  <TextField
                    label={`Typ "${detailJob.order_id}" om te bevestigen`}
                    fullWidth
                    size="small"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    autoFocus
                  />
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetail} disabled={actionPending}>Sluiten</Button>
          {detailJob && !showReject && !showDeleteConfirm && (
            <Button color="error" variant="text" onClick={() => setShowDeleteConfirm(true)} disabled={actionPending}>
              Verwijderen…
            </Button>
          )}
          {detailJob?.workflow_status === 'pending_review' && !showReject && !showDeleteConfirm && (
            <>
              <Button color="error" onClick={() => setShowReject(true)} disabled={actionPending}>Afkeuren…</Button>
              <Button variant="contained" color="success" onClick={approve} disabled={actionPending}>Akkoord & mail klant</Button>
            </>
          )}
          {showReject && (
            <Button color="error" variant="contained" onClick={reject} disabled={actionPending}>Bevestig afkeuring</Button>
          )}
          {showDeleteConfirm && (
            <Button
              color="error"
              variant="contained"
              onClick={deleteJob}
              disabled={actionPending || deleteConfirmText !== String(detailJob?.order_id || '')}
            >
              {actionPending ? 'Bezig...' : 'Definitief verwijderen'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Handmatige job dialog */}
      <Dialog open={manualOpen} onClose={() => !manualBusy && setManualOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nieuwe handmatige job</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Voor orders waarvoor jij de drukproef volledig zelf maakt (bv. compleet eigen ontwerp). De PDF wordt geüpload en de workflow start direct in <strong>Pending review</strong>.
          </Typography>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField label="Order nummer (display)" value={manualForm.order_id} onChange={e => setManualForm({ ...manualForm, order_id: e.target.value })} fullWidth size="small" required />
            <TextField label="Bedrijf" value={manualForm.company} onChange={e => setManualForm({ ...manualForm, company: e.target.value })} fullWidth size="small" required />
            <Stack direction="row" spacing={2}>
              <TextField select label="Factor" value={manualForm.spf} onChange={e => setManualForm({ ...manualForm, spf: e.target.value })} size="small" SelectProps={{ native: true }}>
                <option value="50">SPF 50</option><option value="30">SPF 30</option>
              </TextField>
              <TextField select label="Kleur" value={manualForm.color} onChange={e => setManualForm({ ...manualForm, color: e.target.value })} size="small" SelectProps={{ native: true }}>
                <option value="white">Wit</option><option value="yellow">Geel</option>
              </TextField>
              <TextField select label="Taal" value={manualForm.language} onChange={e => setManualForm({ ...manualForm, language: e.target.value })} size="small" SelectProps={{ native: true }}>
                <option value="NL">NL</option><option value="EN">EN</option>
              </TextField>
            </Stack>
            <Box>
              <Typography variant="caption">Drukproef PDF</Typography>
              <Box><input type="file" accept="application/pdf,.pdf" onChange={e => setManualFile(e.target.files?.[0] || null)} /></Box>
              {manualFile && <Typography variant="caption" color="text.secondary">{manualFile.name} ({Math.round(manualFile.size/1024)} KB)</Typography>}
            </Box>
            {manualErr && <Alert severity="error">{manualErr}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualOpen(false)} disabled={manualBusy}>Annuleren</Button>
          <Button variant="contained" onClick={submitManual} disabled={manualBusy}>
            {manualBusy ? 'Bezig...' : 'Aanmaken'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Drukker batch dialog */}
      <Dialog open={batchOpen} onClose={closeBatch} maxWidth="md" fullWidth>
        <DialogTitle>Batch naar drukker</DialogTitle>
        <DialogContent dividers>
          {!batchPreview && !batchResult && <LinearProgress />}
          {batchPreview?.error && <Alert severity="error">{batchPreview.error}</Alert>}
          {batchPreview && !batchPreview.error && !batchResult && (
            <>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {batchPreview.count === 0
                  ? 'Geen jobs in status "ready_for_print" — wacht op klant-akkoord(en).'
                  : `${batchPreview.count} job(s) klaar om naar de drukker te gaan. Bevestiging onderaan stuurt de mail.`}
              </Typography>
              {batchPreview.count > 0 && (
                <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Order</TableCell>
                        <TableCell>Bedrijf</TableCell>
                        <TableCell>Variant</TableCell>
                        <TableCell align="right">Aantal stickers</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {batchPreview.jobs.map((j) => (
                        <TableRow key={j.id}>
                          <TableCell>{j.order_id}</TableCell>
                          <TableCell>{j.company}</TableCell>
                          <TableCell>SPF{j.spf} {j.color} {j.language}</TableCell>
                          <TableCell align="right">{j.sticker_count ?? '?'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
          {batchResult?.error && <Alert severity="error">{batchResult.error}</Alert>}
          {batchResult?.ok && (
            <Alert severity="success">
              Batch <strong>{batchResult.batch_id}</strong> verstuurd naar {batchResult.mailed_to} ({batchResult.jobs_count} jobs).
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeBatch} disabled={batchSending}>{batchResult?.ok ? 'Sluiten' : 'Annuleren'}</Button>
          {batchPreview && !batchPreview.error && batchPreview.count > 0 && !batchResult && (
            <Button variant="contained" color="warning" onClick={sendBatch} disabled={batchSending}>
              {batchSending ? 'Bezig...' : `Mail drukker (${batchPreview.count} jobs)`}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
